import { IMeterResponse } from "./Meter";
import { IProjectContract } from "./Project";

export interface IMixedUseComponent {
  detailId: number;
  allocationPercent?: number;
  allocationSqFt?: number;
}

/**
 * A matching fact that can be yes, no, or unanswered.
 *
 * Reading a property gives `boolean | null`, where null means nobody has
 * answered -- which is not the same as No, and the matcher treats it
 * differently. Writing takes a string, because PropertyRequest binds these as
 * strings and a JSON boolean will not deserialize:
 *
 *   "Yes" / "No"   the owner is answering
 *   "Unknown"      the owner is un-answering; clears the stored fact
 *   undefined      say nothing; leave whatever is stored alone
 */
export type PropertyTriStateFact = boolean | "Yes" | "No" | "Unknown" | null;

export interface IPropertyContract {
  acreage?: number;
  address: string;
  age?: number;
  yearBuilt?: number;
  bbl?: string;
  bin?: string;
  blobImageUrl?: string;
  buildingTypeId?: number;
  city: string;
  companyId: number;
  county?: string;
  espmPropertyTypeCategoryId?: number | null;
  espmPropertyTypeDetailId?: number | null;
  floors?: number;
  hackProfileImageName?: string;
  hvac?: string;
  hvacPrimaryTypeIds?: number[];
  isWifiAvailable?: boolean;
  // Cloned by the admin sandbox tab (Sandbox:SourcePropertyId lineage), not a customer building.
  isSandbox?: boolean;
  imageBlobKey?: string;
  landTypeId?: number;
  latitude?: number;
  longitude?: number;
  meters?: IMeterResponse[];
  mixedUseComponents?: IMixedUseComponent[] | null;
  monitors?: IMonitorContract[];
  paysBill?: string;
  projects: IProjectContract[];
  propertyId?: number;
  propertyName: string;
  propertyTypeId: number;
  roofSize?: number;
  sqFt?: number;
  state: string;
  suite: string;
  units?: number;
  utilityIds?: number[];
  priorWorkTypeIds?: number[];
  zip: string;
  electricUtilityProvider?: string;
  gasUtilityProvider?: string;

  // IMPL-002 task F -- the matching facts an owner states about their own
  // building. Backed by Common__PropertySettings rather than columns on the
  // property, the same as the two providers above. Omitting a field leaves the
  // stored fact alone; an empty string / empty array clears it.
  ownerClass?: string;
  buildingUses?: string[] | null;
  isHistoric?: PropertyTriStateFact;
  isBarn?: PropertyTriStateFact;
  isAffordableHousing?: PropertyTriStateFact;
  isIncomeQualified?: PropertyTriStateFact;
  isPrimaryResidence?: PropertyTriStateFact;
}

export interface IPropertyBblBinRequest {
  propertyId: number;
  bbl?: string;
  bin?: string;
}

export interface IMonitorContract {
  propertyId: number;
  monitorTypeId: number;
}

// MTR-P0-04 metering attestation (task-4048, spec v0.3). Answered per service (electric, gas,
// ...): who holds the accounts for that service. Values match the API's ServiceMeteringProfile
// and the DB CHECK on Common__PropertyServiceProfile.MeteringProfile. UNCONFIRMED ("never
// asked") is not an answer; SINGLE/MULTI meter and HYBRID are derived, never chosen.
export const METERING_PROFILE = {
  MasterMetered: "MASTER_METERED",
  DirectMetered: "DIRECT_METERED",
  NotPresent: "NOT_PRESENT",
  Unknown: "UNKNOWN",
} as const;

export const METERING_PROFILE_OPTIONS: { value: string; label: string }[] = [
  { value: METERING_PROFILE.MasterMetered, label: "The owner holds all the accounts" },
  { value: METERING_PROFILE.DirectMetered, label: "Tenants hold their own accounts" },
  { value: METERING_PROFILE.NotPresent, label: "Not used at this property" },
  { value: METERING_PROFILE.Unknown, label: "Not sure" },
];

// A meter count is only asked for services that are in use and known.
export const profileTakesMeterCount = (profile: string) =>
  profile === METERING_PROFILE.MasterMetered || profile === METERING_PROFILE.DirectMetered;

export const METERING_ATTESTATION_STATUS = {
  AwaitingAnswer: "AWAITING_ANSWER",
  AnsweredNotSure: "ANSWERED_NOT_SURE",
  Confirmed: "CONFIRMED",
} as const;

export interface IServiceProfileContract {
  utilityTypeId: number;
  serviceCode: string;
  serviceName: string;
  meteringProfile: string;
  expectedMeterCount?: number | null;
  confirmedBy?: number | null;
  confirmedDate?: string | null;
  attestationStatus: string;
  liveMeterCount: number;
}

export interface IMeteringAttestationContract {
  propertyId: number;
  propertyName: string;
  companyId: number;
  companyName: string;
  address?: string;
  city?: string;
  state?: string;
  attestationStatus: string;
  /** Derived: SINGLE_METER / MULTI_METER, or null while any count is unknown. */
  meteringStructure?: string | null;
  isHybrid: boolean;
  liveMeterCount: number;
  services: IServiceProfileContract[];
}

/** A steward work item (task-4063): a live utility meter still UNASSIGNED. Derived by the API, never stored. */
export interface IUnassignedMeterContract {
  meterId: number;
  propertyId: number;
  propertyName: string;
  companyId: number;
  companyName: string;
  city?: string | null;
  state?: string | null;
  meterUid?: string | null;
  meterNumber?: string | null;
  /** SAID for UtilityAPI meters. */
  serviceAccountNumber?: string | null;
  serviceAddress?: string | null;
  serviceCode?: string | null;
  serviceName?: string | null;
  partnerId?: number | null;
  /** UtilityAPI / Bayou / Manual upload / Link Utility */
  source: string;
  rollupRole: string;
  connectionStatus: string;
  lastReportedDate?: string | null;
  createdDate?: string | null;
  daysWaiting?: number | null;
}

export interface IServiceAttestationAnswer {
  utilityTypeId: number;
  meteringProfile: string;
  /** Optional; blank = not known yet. */
  expectedMeterCount?: number | null;
}

export interface IMeteringAttestationRequest {
  services: IServiceAttestationAnswer[];
}
